import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next';
function TransactionList({ transactions }) {
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    startDate: '',
    endDate: ''
  })
const { t } = useTranslation();
  const [categories, setCategories] = useState([])
  const [filteredTransactions, setFilteredTransactions] = useState([])

  useEffect(() => {
    const uniqueCategories = Array.from(new Set(transactions.map(t => t.category)))
    setCategories(uniqueCategories)
  }, [transactions])

  useEffect(() => {
    const filtered = transactions.filter(t => {
      const date = new Date(t.createdAt)
      const startMatch = filters.startDate ? new Date(filters.startDate) <= date : true
      const endMatch = filters.endDate ? new Date(filters.endDate) >= date : true
      const type = t.type || t.action
      const typeMatch = filters.type === 'all' || type === filters.type
      const categoryMatch = filters.category === 'all' || t.category === filters.category
      return startMatch && endMatch && typeMatch && categoryMatch
    })

    setFilteredTransactions(filtered)
  }, [transactions, filters])

  const summary = filteredTransactions.reduce(
    (acc, t) => {
      const type = t.type || t.action
      if (type === 'income') acc.totalEarning += t.amount
      else if (type === 'expense') acc.totalCharge += t.amount
      return acc
    },
    { totalEarning: 0, totalCharge: 0 }
  )

  summary.profit = summary.totalEarning - summary.totalCharge

  const handleFilterChange = e => {
    const { name, value } = e.target
    setFilters(prev => ({ ...prev, [name]: value }))
  }

  return (
  <div className="card">
  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
    {t('transactionHistory.title')}
  </h3>

  {/* Filters */}
  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md mb-4">
    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('transactionHistory.filtersTitle')}</h4>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <label htmlFor="type-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('transactionHistory.filters.type')}
        </label>
        <select
          id="type-filter"
          name="type"
          value={filters.type}
          onChange={handleFilterChange}
          className="input-field text-sm py-1"
        >
          <option value="all">{t('transactionHistory.filters.allTypes')}</option>
          <option value="income">{t('transactionHistory.filters.income')}</option>
          <option value="expense">{t('transactionHistory.filters.expense')}</option>
        </select>
      </div>

      <div>
        <label htmlFor="category-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('transactionHistory.filters.category')}
        </label>
        <select
          id="category-filter"
          name="category"
          value={filters.category}
          onChange={handleFilterChange}
          className="input-field text-sm py-1"
        >
          <option value="all">{t('transactionHistory.filters.allCategories')}</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="start-date" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('transactionHistory.filters.fromDate')}
        </label>
        <input
          type="date"
          id="start-date"
          name="startDate"
          value={filters.startDate}
          onChange={handleFilterChange}
          className="input-field text-sm py-1"
        />
      </div>

      <div>
        <label htmlFor="end-date" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('transactionHistory.filters.toDate')}
        </label>
        <input
          type="date"
          id="end-date"
          name="endDate"
          value={filters.endDate}
          onChange={handleFilterChange}
          className="input-field text-sm py-1"
        />
      </div>
    </div>
  </div>

  {/* Summary */}
  <div className="grid grid-cols-3 gap-4 mb-4">
    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
      <p className="text-xs text-green-800 dark:text-green-200">{t('transactionHistory.summary.totalIncome')}</p>
      <p className="text-lg font-semibold text-green-700 dark:text-green-300">€{summary.totalEarning.toFixed(2)}</p>
    </div>

    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
      <p className="text-xs text-red-800 dark:text-red-200">{t('transactionHistory.summary.totalExpenses')}</p>
      <p className="text-lg font-semibold text-red-700 dark:text-red-300">€{summary.totalCharge.toFixed(2)}</p>
    </div>

    <div className={`p-3 rounded-md ${
      summary.profit >= 0 
        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
        : 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
    }`}>
      <p className="text-xs">{t('transactionHistory.summary.balance')}</p>
      <p className="text-lg font-semibold">€{summary.profit.toFixed(2)}</p>
    </div>
  </div>

  {/* Table */}
  {filteredTransactions.length > 0 ? (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              {t('transactionHistory.tableHeaders.date')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              {t('transactionHistory.tableHeaders.description')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              {t('transactionHistory.tableHeaders.category')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              {t('transactionHistory.tableHeaders.amount')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {filteredTransactions.map((transaction, index) => {
            const type = transaction.type || transaction.action;
            const amount = transaction.amount ?? 0;

            return (
              <tr
                key={index}
                className={type === 'income' ? 'bg-green-50/30 dark:bg-green-900/10' : 'bg-red-50/30 dark:bg-red-900/10'}
              >
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {transaction.createdAt
                    ? format(new Date(transaction.createdAt), 'dd/MM/yyyy')
                    : t('transactionHistory.tableData.na')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 description-cell">{transaction.description}</td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{transaction.category}</td>
                <td
                  className={`px-4 py-3 whitespace-nowrap text-sm font-medium text-right ${
                    type === 'income'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {`${type === 'income' ? '+' : '-'}€${Math.abs(amount).toFixed(2)}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
      {t('transactionHistory.noTransactions')}
    </div>
  )}
</div>

  )
}

export default TransactionList
